import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as projectApi from '../Api/projectApi';

const PROJECT_KEY = 'projects';

const unwrapApiData = (response) => response?.data ?? response;

const isPopulatedUser = (user) =>
	user &&
	typeof user === 'object' &&
	(user.firstName || user.lastName || user.email);

const preserveUserRef = (incoming, existing) =>
	isPopulatedUser(incoming) ? incoming : existing ?? incoming;

const preserveUserList = (incoming = [], existing = []) => {
	if (!incoming.length) return existing;
	return incoming.map((item, index) => {
		if (isPopulatedUser(item)) return item;
		const match = existing.find(
			(entry) => String(entry?._id ?? entry) === String(item?._id ?? item),
		);
		const fallback = existing[index];
		const preserved = isPopulatedUser(match)
			? match
			: isPopulatedUser(fallback)
				? fallback
				: item;
		return preserved;
	});
};

const reactionUserId = (reaction) =>
	reaction?.userId?._id ?? reaction?.userId;

const preserveReactions = (incoming, existing = []) => {
	if (!Array.isArray(incoming)) return existing;
	if (incoming.length === 0) return incoming;
	return incoming.map((reaction, index) => {
		const prior =
			existing.find(
				(entry) =>
					String(reactionUserId(entry)) === String(reactionUserId(reaction)) &&
					entry.emoji === reaction.emoji,
			) ?? existing[index];
		return {
			...reaction,
			userId: preserveUserRef(reaction.userId, prior?.userId),
		};
	});
};

const mergeReplyPreservingPopulation = (incoming, existing) => {
	if (!incoming) return existing;
	if (!existing) return incoming;

	return {
		...existing,
		...incoming,
		author: preserveUserRef(incoming.author, existing.author),
		mentions: preserveUserList(incoming.mentions, existing.mentions),
		attachments: incoming.attachments ?? existing.attachments,
		reactions: preserveReactions(incoming.reactions, existing.reactions),
		body: incoming.body ?? existing.body,
		updatedAt: incoming.updatedAt ?? existing.updatedAt,
		editCount: incoming.editCount ?? existing.editCount,
		editHistory: incoming.editHistory ?? existing.editHistory,
	};
};

const mergeCommentPreservingPopulation = (incoming, existing) => {
	if (!incoming) return existing;
	if (!existing) return incoming;

	const existingReplies = existing.replies || [];
	const mergedReplies = (incoming.replies || []).map((reply) => {
		const prior = existingReplies.find(
			(entry) => String(entry._id) === String(reply._id),
		);
		return mergeReplyPreservingPopulation(reply, prior);
	});

	return {
		...existing,
		...incoming,
		author: preserveUserRef(incoming.author, existing.author),
		mentions: preserveUserList(incoming.mentions, existing.mentions),
		linkedTasks: incoming.linkedTasks ?? existing.linkedTasks,
		linkedSubtasks: incoming.linkedSubtasks ?? existing.linkedSubtasks,
		attachments: incoming.attachments ?? existing.attachments,
		reactions: preserveReactions(incoming.reactions, existing.reactions),
		replies: mergedReplies.length ? mergedReplies : existingReplies,
		body: incoming.body ?? existing.body,
		updatedAt: incoming.updatedAt ?? existing.updatedAt,
		editCount: incoming.editCount ?? existing.editCount,
		editHistory: incoming.editHistory ?? existing.editHistory,
	};
};

const mergeCommentsPreservingPopulation = (incoming = [], existing = []) => {
	if (!incoming.length) return existing;
	return incoming.map((comment) => {
		const prior = existing.find(
			(entry) => String(entry._id) === String(comment._id),
		);
		return mergeCommentPreservingPopulation(comment, prior);
	});
};

const mergeCommentEntityIntoProjectCache = (
	queryClient,
	projectId,
	response,
	{ taskId, subtaskId },
) => {
	const entity = unwrapApiData(response);
	if (!entity || !projectId) return;

	queryClient.setQueryData([PROJECT_KEY, projectId], (oldProject) => {
		if (!oldProject?.tasks) return oldProject;

		const tasks = oldProject.tasks.map((task) => {
			if (subtaskId) {
				if (String(task._id) !== String(taskId)) return task;
				return {
					...task,
					subtasks: (task.subtasks || []).map((sub) =>
						String(sub._id) === String(subtaskId)
							? {
									...sub,
									comments: mergeCommentsPreservingPopulation(
										entity.comments,
										sub.comments,
									),
								}
							: sub,
					),
				};
			}

			if (String(task._id) === String(taskId || entity._id)) {
				return {
					...task,
					comments: mergeCommentsPreservingPopulation(
						entity.comments,
						task.comments,
					),
				};
			}

			return task;
		});

		return { ...oldProject, tasks };
	});
};

const handleCommentMutationSuccess = (queryClient, response, variables) => {
	mergeCommentEntityIntoProjectCache(
		queryClient,
		variables.projectId,
		response,
		variables,
	);
};

export const useCompanyProjects = (companyId) =>
	useQuery({
		queryKey: [PROJECT_KEY, 'company', companyId],
		queryFn: async () => {
			const response = await projectApi.getCompanyProjects(companyId);
			const data = response.data || response;
			// Ensure we always return an array
			if (Array.isArray(data)) return data;
			if (Array.isArray(data?.projects)) return data.projects;
			if (Array.isArray(data?.data)) return data.data;
			return [];
		},
		enabled: !!companyId,
	});

export const useUserProjects = (employeeId) =>
	useQuery({
		queryKey: [PROJECT_KEY, 'user', employeeId],
		queryFn: async () => {
			const response = await projectApi.getUserProjects(employeeId);
			const data = response.data || response;
			// Ensure we always return an array
			if (Array.isArray(data)) return data;
			if (Array.isArray(data?.projects)) return data.projects;
			if (Array.isArray(data?.data)) return data.data;
			return [];
		},
		enabled: !!employeeId,
	});

export const useProject = (projectId, companyId, queryOptions = {}) => {
	const projectQuery = useQuery({
		queryKey: [PROJECT_KEY, projectId],
		queryFn: async () => {
			const response = await projectApi.getProjectById(projectId, companyId);
			// Extract the actual project data from the response
			return response.data || response;
		},
		enabled: !!projectId && !!companyId,
		...queryOptions,
	});

	const addDocumentMutation = useAddDocument();

	return {
		...projectQuery,
		addDocumentMutation,
	};
};

export const useCreateProject = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ data, companyId }) =>
			projectApi.createProject(data, companyId),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, 'company', variables.companyId],
			});
		},
	});
};

export const useUpdateProject = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, data, companyId }) =>
			projectApi.updateProject(projectId, data, companyId),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, 'company', variables.companyId],
			});
		},
	});
};

export const useDeleteProject = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, companyId }) =>
			projectApi.deleteProject(projectId, companyId),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, 'company', variables.companyId],
			});
		},
	});
};

export const useUpdateTaskStatus = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, status, reviewer }) =>
			projectApi.updateTaskStatus(projectId, taskId, status, reviewer),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
			queryClient.invalidateQueries({ queryKey: [PROJECT_KEY, 'user'] });
		},
	});
};

export const useUpdateSubtaskStatus = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, subtaskId, status, reviewer }) =>
			projectApi.updateSubtaskStatus(projectId, taskId, subtaskId, status, reviewer),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
			queryClient.invalidateQueries({ queryKey: [PROJECT_KEY, 'user'] });
		},
	});
};

export const useUpdateTaskAssignees = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, subtaskId, assignedTo }) =>
			projectApi.updateTaskAssignees(projectId, taskId, assignedTo, subtaskId),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
			queryClient.invalidateQueries({ queryKey: [PROJECT_KEY, 'user'] });
			queryClient.invalidateQueries({ queryKey: [PROJECT_KEY, 'company'] });
		},
	});
};

export const useUpdateTaskPriority = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, subtaskId, priority }) =>
			projectApi.updateTaskPriority(projectId, taskId, priority, subtaskId),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
			queryClient.invalidateQueries({ queryKey: [PROJECT_KEY, 'user'] });
			queryClient.invalidateQueries({ queryKey: [PROJECT_KEY, 'company'] });
		},
	});
};

export const useAddDocument = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ companyId, projectId, documentData }) =>
			projectApi.addDocument(companyId, projectId, documentData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useAddRisk = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ companyId, projectId, riskData }) =>
			projectApi.addRisk(companyId, projectId, riskData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useUpdateRiskStatus = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			companyId,
			projectId,
			riskId,
			status,
			note,
			probability,
			impact,
			category,
		}) =>
			projectApi.updateRiskStatus(
				companyId,
				projectId,
				riskId,
				status,
				note,
				probability,
				impact,
				category,
			),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useAddTask = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskData }) =>
			projectApi.addTask(projectId, taskData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useAddSubtask = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, subtaskData }) =>
			projectApi.addSubtask(projectId, taskId, subtaskData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useAddTaskFile = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, fileData }) =>
			projectApi.addTaskFile(projectId, taskId, fileData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useDeleteTaskFile = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, fileId }) =>
			projectApi.deleteTaskFile(projectId, taskId, fileId),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useAddSubtaskFile = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, subtaskId, fileData }) =>
			projectApi.addSubtaskFile(projectId, taskId, subtaskId, fileData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useDeleteSubtaskFile = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, taskId, subtaskId, fileId }) =>
			projectApi.deleteSubtaskFile(projectId, taskId, subtaskId, fileId),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

const useCommentMutation = (mutationFn) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: (data, variables) => {
			handleCommentMutationSuccess(queryClient, data, variables);
		},
	});
};

export const useAddTaskComment = () =>
	useCommentMutation(({ projectId, taskId, payload }) =>
		projectApi.addTaskComment(projectId, taskId, payload),
	);

export const useDeleteTaskComment = () =>
	useCommentMutation(({ projectId, taskId, commentId }) =>
		projectApi.deleteTaskComment(projectId, taskId, commentId),
	);

export const useAddSubtaskComment = () =>
	useCommentMutation(({ projectId, taskId, subtaskId, payload }) =>
		projectApi.addSubtaskComment(projectId, taskId, subtaskId, payload),
	);

export const useDeleteSubtaskComment = () =>
	useCommentMutation(({ projectId, taskId, subtaskId, commentId }) =>
		projectApi.deleteSubtaskComment(projectId, taskId, subtaskId, commentId),
	);

export const useAddTaskCommentReply = () =>
	useCommentMutation(({ projectId, taskId, commentId, payload }) =>
		projectApi.addTaskCommentReply(projectId, taskId, commentId, payload),
	);

export const useDeleteTaskCommentReply = () =>
	useCommentMutation(({ projectId, taskId, commentId, replyId }) =>
		projectApi.deleteTaskCommentReply(projectId, taskId, commentId, replyId),
	);

export const useAddSubtaskCommentReply = () =>
	useCommentMutation(({ projectId, taskId, subtaskId, commentId, payload }) =>
		projectApi.addSubtaskCommentReply(
			projectId,
			taskId,
			subtaskId,
			commentId,
			payload,
		),
	);

export const useDeleteSubtaskCommentReply = () =>
	useCommentMutation(({ projectId, taskId, subtaskId, commentId, replyId }) =>
		projectApi.deleteSubtaskCommentReply(
			projectId,
			taskId,
			subtaskId,
			commentId,
			replyId,
		),
	);

export const useEditTaskComment = () =>
	useCommentMutation(({ projectId, taskId, commentId, payload }) =>
		projectApi.editTaskComment(projectId, taskId, commentId, payload),
	);

export const useEditSubtaskComment = () =>
	useCommentMutation(({ projectId, taskId, subtaskId, commentId, payload }) =>
		projectApi.editSubtaskComment(
			projectId,
			taskId,
			subtaskId,
			commentId,
			payload,
		),
	);

export const useEditTaskCommentReply = () =>
	useCommentMutation(({ projectId, taskId, commentId, replyId, payload }) =>
		projectApi.editTaskCommentReply(
			projectId,
			taskId,
			commentId,
			replyId,
			payload,
		),
	);

export const useEditSubtaskCommentReply = () =>
	useCommentMutation(({ projectId, taskId, subtaskId, commentId, replyId, payload }) =>
		projectApi.editSubtaskCommentReply(
			projectId,
			taskId,
			subtaskId,
			commentId,
			replyId,
			payload,
		),
	);

export const useToggleTaskCommentReaction = () =>
	useCommentMutation(({ projectId, taskId, commentId, emoji }) =>
		projectApi.toggleTaskCommentReaction(projectId, taskId, commentId, emoji),
	);

export const useToggleSubtaskCommentReaction = () =>
	useCommentMutation(({ projectId, taskId, subtaskId, commentId, emoji }) =>
		projectApi.toggleSubtaskCommentReaction(
			projectId,
			taskId,
			subtaskId,
			commentId,
			emoji,
		),
	);

export const useToggleTaskCommentReplyReaction = () =>
	useCommentMutation(({ projectId, taskId, commentId, replyId, emoji }) =>
		projectApi.toggleTaskCommentReplyReaction(
			projectId,
			taskId,
			commentId,
			replyId,
			emoji,
		),
	);

export const useToggleSubtaskCommentReplyReaction = () =>
	useCommentMutation(({ projectId, taskId, subtaskId, commentId, replyId, emoji }) =>
		projectApi.toggleSubtaskCommentReplyReaction(
			projectId,
			taskId,
			subtaskId,
			commentId,
			replyId,
			emoji,
		),
	);

export const useAddTeamMember = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, memberData }) =>
			projectApi.addTeamMember(projectId, memberData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useRemoveTeamMember = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, memberId, reason }) =>
			projectApi.removeTeamMember(projectId, memberId, reason),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useAddInvoice = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, invoiceData }) =>
			projectApi.addInvoice(projectId, invoiceData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
			// Also invalidate project invoices query
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, 'invoices', variables.projectId],
			});
		},
	});
};

export const useProjectInvoices = (projectId) =>
	useQuery({
		queryKey: [PROJECT_KEY, 'invoices', projectId],
		queryFn: async () => {
			const response = await projectApi.getProjectInvoices(projectId);
			return response.data || response;
		},
		enabled: !!projectId,
	});

export const useClientProjects = (clientId) =>
	useQuery({
		queryKey: [PROJECT_KEY, 'client', clientId],
		queryFn: async () => {
			const response = await projectApi.getClientProjects(clientId);
			return response.data || response;
		},
		enabled: !!clientId,
	});

export const useCreateMinimalistProject = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ companyId, projectData }) =>
			projectApi.createMinimalistProject(companyId, projectData),
		onSuccess: (response, variables) => {
			// Invalidate relevant queries
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, 'client', variables.projectData.clientId],
			});
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, 'company', variables.companyId],
			});
			// Return the actual project data from the response
			return response.data?.data || response.data || response;
		},
	});
};

// Milestone hooks
export const useAddMilestone = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, milestoneData }) =>
			projectApi.addMilestone(projectId, milestoneData),
		onSuccess: (response, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useUpdateMilestone = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, milestoneId, milestoneData }) =>
			projectApi.updateMilestone(projectId, milestoneId, milestoneData),
		onSuccess: (response, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useMarkMilestoneComplete = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, milestoneId }) =>
			projectApi.markMilestoneComplete(projectId, milestoneId),
		onSuccess: (response, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useDeleteMilestone = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, milestoneId }) =>
			projectApi.deleteMilestone(projectId, milestoneId),
		onSuccess: (response, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

// Hour tracking hooks
export const useHourEntries = (projectId, teamMemberId) =>
	useQuery({
		queryKey: [PROJECT_KEY, 'hours', projectId, teamMemberId],
		queryFn: async () => {
			const response = await projectApi.getHourEntries(projectId, teamMemberId);
			const data = response.data || response;
			// Ensure we always return an array
			return Array.isArray(data) ? data : [];
		},
		enabled: !!projectId && !!teamMemberId,
	});

export const useAddHourEntry = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, teamMemberId, hourData }) =>
			projectApi.addHourEntry(projectId, teamMemberId, hourData),
		onSuccess: (response, variables) => {
			// Invalidate hour entries query
			queryClient.invalidateQueries({
				queryKey: [
					PROJECT_KEY,
					'hours',
					variables.projectId,
					variables.teamMemberId,
				],
			});
			// Also invalidate the project query to update actualHours
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useUpdateHourEntry = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, teamMemberId, hourEntryId, hourData }) =>
			projectApi.updateHourEntry(projectId, teamMemberId, hourEntryId, hourData),
		onSuccess: (response, variables) => {
			// Invalidate hour entries query
			queryClient.invalidateQueries({
				queryKey: [
					PROJECT_KEY,
					'hours',
					variables.projectId,
					variables.teamMemberId,
				],
			});
			// Also invalidate the project query to update actualHours
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};

export const useDeleteHourEntry = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ projectId, teamMemberId, hourEntryId }) =>
			projectApi.deleteHourEntry(projectId, teamMemberId, hourEntryId),
		onSuccess: (response, variables) => {
			// Invalidate hour entries query
			queryClient.invalidateQueries({
				queryKey: [
					PROJECT_KEY,
					'hours',
					variables.projectId,
					variables.teamMemberId,
				],
			});
			// Also invalidate the project query to update actualHours
			queryClient.invalidateQueries({
				queryKey: [PROJECT_KEY, variables.projectId],
			});
		},
	});
};
